import type { Db } from '@shared/storage';
import { DEXIE_DB_NAME, QUERY_CACHE_DB_NAME } from '@shared/storage';
import { responseByteSize } from '@shared/sw/response-size';
import { collectIssueImages } from '@entities/issue';

/**
 * The groups Settings → Offline & storage breaks the app's storage into, in
 * the order the bar draws them (and the order their colours are assigned:
 * a group keeps its colour whether or not the others are present).
 */
export const STORAGE_GROUPS = [
	'app',
	'downloads',
	'images',
	'video',
	'audio',
	'content',
	'other',
] as const;

export type StorageGroup = (typeof STORAGE_GROUPS)[number];

export interface StorageBreakdown {
	/** Bytes per group. */
	groups: Record<StorageGroup, number>;
	/** The sum of `groups`: what the app itself is storing. */
	total: number;
	/** The browser's quota for this origin, when it reports one. */
	quota?: number;
}

/**
 * Measures what the app stores on this device, by reading it -- not
 * `navigator.storage.estimate()`, whose `usage` lags behind deletions by
 * minutes (deleted caches stay counted until the browser frees them) and so
 * goes *up* when the reader clears the cache and the app reloads.
 *
 * Cache Storage is sized per response (`Content-Length`, else the body);
 * IndexedDB per stored value. A cached file that belongs to a downloaded
 * issue counts as Downloaded issues, never as Images too.
 */
export async function measureStorage(db: Db | undefined): Promise<StorageBreakdown> {
	const groups = Object.fromEntries(
		STORAGE_GROUPS.map((group) => [group, 0]),
	) as Record<StorageGroup, number>;

	const downloadedUrls = await downloadedAssetUrls(db);

	if (typeof caches !== 'undefined') {
		for (const name of await caches.keys()) {
			const group = cacheGroup(name);
			const cache = await caches.open(name);
			for (const request of await cache.keys()) {
				const response = await cache.match(request);
				const bytes = response ? ((await responseByteSize(response)) ?? 0) : 0;
				groups[downloadedUrls.has(request.url) ? 'downloads' : group] += bytes;
			}
		}
	}

	for (const name of await databaseNames()) {
		groups[databaseGroup(name)] += await measureDatabase(name);
	}

	groups.other += localStorageBytes();

	const total = STORAGE_GROUPS.reduce((sum, group) => sum + groups[group], 0);
	return { groups, total, quota: await storageQuota() };
}

function cacheGroup(name: string): StorageGroup {
	if (name.endsWith('-images')) {
		return 'images';
	}
	if (name.endsWith('-videos')) {
		return 'video';
	}
	if (name.endsWith('-audios')) {
		return 'audio';
	}
	// The precache (the app's own files), its fonts and shared styles, the
	// navigation cache and the version marker.
	return 'app';
}

function databaseGroup(name: string): StorageGroup {
	if (name === DEXIE_DB_NAME) {
		return 'downloads';
	}
	if (name === QUERY_CACHE_DB_NAME) {
		return 'content';
	}
	return 'other';
}

async function downloadedAssetUrls(db: Db | undefined): Promise<Set<string>> {
	if (!db) {
		return new Set();
	}
	try {
		return new Set(collectIssueImages(await db.getAllArticles()));
	} catch {
		return new Set();
	}
}

async function databaseNames(): Promise<Array<string>> {
	if (typeof indexedDB === 'undefined' || !indexedDB.databases) {
		return [];
	}
	try {
		const listed = await indexedDB.databases();
		return listed.map((database) => database.name).filter((name): name is string => !!name);
	} catch {
		return [];
	}
}

/**
 * Sums every value in every store of an existing database. Only called with
 * names `indexedDB.databases()` listed, so opening never creates one.
 */
function measureDatabase(name: string): Promise<number> {
	return new Promise((resolve) => {
		const request = indexedDB.open(name);
		request.onerror = () => resolve(0);
		request.onblocked = () => resolve(0);
		request.onsuccess = () => {
			const database = request.result;
			const stores = [...database.objectStoreNames];
			if (!stores.length) {
				database.close();
				resolve(0);
				return;
			}
			let bytes = 0;
			const transaction = database.transaction(stores, 'readonly');
			for (const store of stores) {
				transaction.objectStore(store).openCursor().onsuccess = (event) => {
					const cursor = (event.target as IDBRequest<IDBCursorWithValue | null>).result;
					if (cursor) {
						bytes += valueBytes(cursor.key) + valueBytes(cursor.value);
						cursor.continue();
					}
				};
			}
			transaction.oncomplete = () => {
				database.close();
				resolve(bytes);
			};
			transaction.onerror = transaction.onabort = () => {
				database.close();
				resolve(bytes);
			};
		};
	});
}

function valueBytes(value: unknown): number {
	if (value instanceof Blob) {
		return value.size;
	}
	if (value instanceof ArrayBuffer || ArrayBuffer.isView(value)) {
		return value.byteLength;
	}
	if (typeof value === 'string') {
		return new Blob([value]).size;
	}
	try {
		return new Blob([JSON.stringify(value) ?? '']).size;
	} catch {
		return 0;
	}
}

function localStorageBytes(): number {
	try {
		let characters = 0;
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i) ?? '';
			characters += key.length + (localStorage.getItem(key)?.length ?? 0);
		}
		// Browsers store localStorage as UTF-16.
		return characters * 2;
	} catch {
		return 0;
	}
}

async function storageQuota(): Promise<number | undefined> {
	if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
		return undefined;
	}
	try {
		return (await navigator.storage.estimate()).quota;
	} catch {
		return undefined;
	}
}
